import {
    isWeakCommandSearchScore,
    sortCommandSearchResults,
} from '../match-query';
import type {CommandSearchGroup, CommandSearchResult} from '../types';

/** Merges category buckets into a flat, sorted result list. */
export const mergeCommandSearchResults = (
    buckets: Partial<Record<string, CommandSearchResult[]>>,
    categoryOrder: string[],
): CommandSearchResult[] => {
    const merged: CommandSearchResult[] = [];
    categoryOrder.forEach((category) => {
        merged.push(...(buckets[category] ?? []));
    });
    return sortCommandSearchResults(merged);
};

/**
 * Group relevance from best item quality, top-3 consistency, quantity, and weak-match penalty.
 * Category priority is only a light bias so configured order remains a soft tie-break.
 */
export const scoreCommandSearchGroup = (
    group: Pick<CommandSearchGroup, 'items' | 'category'>,
    categoryOrder: string[],
): number => {
    const scores = group.items
        .map((item) => item.score ?? 0)
        .filter((score) => score > 0)
        .sort((a, b) => b - a);

    if (!scores.length) {
        // Loading-only groups stay near the bottom but keep category bias for stable placement.
        const categoryIndex = categoryOrder.indexOf(group.category);
        const categoryPriorityBias = categoryIndex >= 0
            ? Math.max(0, 4 - categoryIndex * 0.15)
            : 0;
        return categoryPriorityBias;
    }

    const bestItemScore = scores[0] ?? 0;
    const top3 = scores.slice(0, 3);
    const averageTop3Score = top3.reduce((sum, score) => sum + score, 0) / top3.length;
    const resultCount = group.items.length;
    const weakMatchRatio = group.items.filter((item) => isWeakCommandSearchScore(item.score)).length
        / Math.max(1, resultCount);

    const categoryIndex = categoryOrder.indexOf(group.category);
    // Soft bias only — never enough to override a strong relevance gap.
    const categoryPriorityBias = categoryIndex >= 0
        ? Math.max(0, 4 - categoryIndex * 0.15)
        : 0;

    // Recent gets a small sticky bias so it stays visible, but not enough to beat exact matches.
    const recentBias = group.category === 'recent' ? 6 : 0;

    return (
        bestItemScore * 0.65
        + averageTop3Score * 0.25
        + Math.log2(resultCount + 1) * 4
        - weakMatchRatio * 12
        + categoryPriorityBias
        + recentBias
    );
};

export interface GroupCommandSearchResultsOptions {
    /** When true (default for non-empty queries), sort groups by relevance score. */
    sortByRelevance?: boolean;
}

/** Groups flat results by category with loading state per group. */
export const groupCommandSearchResults = (
    results: CommandSearchResult[],
    loadingCategories: Set<string>,
    categoryOrder: string[],
    categoryLabels: Record<string, string>,
    options?: GroupCommandSearchResultsOptions,
): CommandSearchGroup[] => {
    const groups: CommandSearchGroup[] = [];

    categoryOrder.forEach((category) => {
        const items = results.filter((result) => result.category === category);
        const loading = loadingCategories.has(category);
        if (!items.length && !loading) {
            return;
        }
        groups.push({
            category,
            label: categoryLabels[category] ?? category,
            items: sortCommandSearchResults(items),
            loading,
        });
    });

    if (!options?.sortByRelevance) {
        return groups;
    }

    return [...groups].sort((a, b) => {
        const scoreDelta = scoreCommandSearchGroup(b, categoryOrder) - scoreCommandSearchGroup(a, categoryOrder);
        if (scoreDelta !== 0) {
            return scoreDelta;
        }
        const orderDelta = categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category);
        if (orderDelta !== 0) {
            return orderDelta;
        }
        return a.label.localeCompare(b.label);
    });
};

/** Filters out results from superseded provider ids. */
export const applySupersedes = (
    results: CommandSearchResult[],
    activeProviderIds: Set<string>,
    supersededBy: Map<string, Set<string>>,
): CommandSearchResult[] => {
    const hiddenProviderIds = new Set<string>();

    activeProviderIds.forEach((providerId) => {
        supersededBy.get(providerId)?.forEach((id) => hiddenProviderIds.add(id));
    });

    if (!hiddenProviderIds.size) {
        return results;
    }

    return results.filter((result) => !result.providerId || !hiddenProviderIds.has(result.providerId));
};
